import { makeAutoObservable, observable, runInAction } from 'mobx';
import type {
  GroupEntry,
  GroupDashboardResponse,
} from '../types/charts/GroupDashboardData';
import { STATUS_API } from 'src/config';
import { EmfitAPI } from '../services/BaseAPI';
import { applyDayValidityMask } from '../utils/groupDashboardMask';

type State = {
  groupData: GroupEntry[];
  loading: boolean;
  error: string | null;
  days: number; // Number of days to fetch (7, 14, 21, 28, 35)
  /** Days the data currently in groupData actually covers; 0 when empty. */
  loadedDays: number;
  selectedGroupId: number | null;
  isMasterUser: boolean;
};

/**
 * GroupDashboardStore
 * Manages group dashboard data for multiple devices
 */
export class GroupDashboardStoreClass {
  private _state: State = observable({
    groupData: [],
    loading: false,
    error: null,
    days: 7, // Default to 1 week
    loadedDays: 0,
    selectedGroupId: null,
    isMasterUser: false,
  });

  /**
   * Issue counter for in-flight fetches. Only the most recently *issued*
   * request is allowed to write, so a slow 7-day response cannot land on top
   * of the 35 days the timelines asked for afterwards.
   */
  private _issued = 0;
  private _inFlight: Promise<void> | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get groupData() {
    return this._state.groupData;
  }

  get loading() {
    return this._state.loading;
  }

  get error() {
    return this._state.error;
  }

  get days() {
    return this._state.days;
  }

  /** How many days the loaded data covers. 0 when nothing has loaded yet. */
  get loadedDays() {
    return this._state.loadedDays;
  }

  /**
   * Fetch only if the loaded data does not already cover `days`.
   *
   * Views used to guard on `groupData.length === 0`, which made coverage a
   * race: Main fetches 7 days at mount and the timelines want 35, so whichever
   * response landed first decided how far back the user could page.
   */
  async ensureDays(days: number): Promise<void> {
    if (this._inFlight) await this._inFlight;
    if (this._state.loadedDays >= days) return;
    await this.fetchGroupDashboard(days);
  }

  setDays(days: number) {
    this._state.days = days;
    // Automatically refetch when days change
    this.fetchGroupDashboard();
  }

  get selectedGroupId(): number | null {
    return this._state.selectedGroupId;
  }

  setSelectedGroupId(groupId: number | null): void {
    this._state.selectedGroupId = groupId;
    this.fetchGroupDashboard();
  }

  setMasterUser(isMasterUser: boolean): void {
    this._state.isMasterUser = isMasterUser;
  }

  /**
   * Fetch group dashboard data for all devices
   * @param days - Number of days to fetch (default: current setting)
   */
  async fetchGroupDashboard(days?: number) {
    const daysToFetch = days !== undefined ? days : this._state.days;
    const issue = ++this._issued;

    this._state.loading = true;
    this._state.error = null;

    const request = this._fetchGroupDashboard(daysToFetch, days, issue);
    this._inFlight = request.finally(() => {
      if (issue === this._issued) this._inFlight = null;
    });
    return this._inFlight;
  }

  private async _fetchGroupDashboard(
    daysToFetch: number,
    days: number | undefined,
    issue: number,
  ) {
    try {
      // Master users must call group-dashboard — their token is rejected by group-user-dashboard.
      // group-dashboard without a groupId returns all devices for the master user.
      const url = this._state.selectedGroupId
        ? `${STATUS_API}/v1/group-dashboard?days=${daysToFetch}&groupId=${this._state.selectedGroupId}`
        : this._state.isMasterUser
          ? `${STATUS_API}/v1/group-dashboard?days=${daysToFetch}`
          : `${STATUS_API}/v1/group-user-dashboard?days=${daysToFetch}`;

      const data = await EmfitAPI.get<GroupDashboardResponse>(url);

      runInAction(() => {
        // A later request has already been issued; its answer is the one the
        // caller is waiting for, so this one must not overwrite it.
        if (issue !== this._issued) return;
        if (!data) return;
        // Blank out days the device has no data for, so a missing night draws
        // a gap instead of a point at zero.
        this._state.groupData = data.map(applyDayValidityMask);
        this._state.loadedDays = daysToFetch;
        if (days !== undefined) {
          this._state.days = days;
        }
        this._state.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        if (issue !== this._issued) return;
        this._state.error =
          error instanceof Error
            ? error.message
            : 'Failed to fetch group dashboard';
        this._state.loading = false;
      });
    }
  }

  /**
   * Find group data for a specific device by serial number
   */
  getDeviceData(serialNumber: string): GroupEntry | undefined {
    return this._state.groupData.find(
      (entry) => entry.device.serial === serialNumber
    );
  }

  /**
   * Clear all data
   */
  clear() {
    this._state.groupData = [];
    this._state.loadedDays = 0;
    this._state.error = null;
  }

  /**
   * Drops everything belonging to the current login. Called from
   * resetStores() on logout, so the next login starts empty.
   */
  reset(): void {
    this._state.groupData = [];
    this._state.loading = false;
    this._state.error = null;
    this._state.days = 7;
    this._state.loadedDays = 0;
    this._state.selectedGroupId = null;
    this._state.isMasterUser = false;
    // Any fetch still in flight must not write into the next login's state.
    this._issued += 1;
    this._inFlight = null;
  }
}

export const GroupDashboardStore = new GroupDashboardStoreClass();
